import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowLeft, GripVertical, LayoutTemplate, Plus, Trash2, Code, Eye, Maximize2, Minimize2,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/lib/picker-search";
import type { LandingPageTemplate } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Reorder } from "framer-motion";

const SECTION_LABELS: Record<string, string> = {
  announcement_bar: "Announcement Bar",
  hero_buy_box: "Hero / Buy Box",
  sticky_buy_bar: "Sticky Buy Bar",
  product_recipe: "Product Cards & Recipe",
  how_to_use: "How to Use",
  science_research: "Science & Research",
  belief_shift_competitor: "Belief Shift / Competitor",
  farmstand_story: "Origin Story",
  timeline_results: "Timeline & Results",
  quiz_funnel: "Quiz Funnel",
  reviews_grid: "Reviews",
  faq_accordion: "FAQ",
  final_cta: "Final CTA",
};

const DEFAULT_SECTION_HTML: Record<string, string> = {
  announcement_bar: `<div style="background:#1a2332;color:#fff;padding:10px 0;overflow:hidden;font-size:13px;letter-spacing:0.02em;font-family:'Inter',sans-serif">
  <div style="display:flex;justify-content:center;gap:24px;white-space:nowrap">
    <span style="opacity:0.9">Always handmade</span>
    <span style="opacity:0.4">·</span>
    <span style="opacity:0.9">Always small-batch</span>
    <span style="opacity:0.4">·</span>
    <span style="opacity:0.9">Always shipped fresh</span>
  </div>
</div>`,
  hero_buy_box: `<div style="background:linear-gradient(135deg,#f8f6f0 0%,#f0ece0 100%);padding:60px 24px;text-align:center;font-family:'Inter',sans-serif">
  <div style="max-width:680px;margin:0 auto">
    <div style="display:inline-block;background:#c53030;color:#fff;padding:6px 16px;border-radius:20px;font-size:13px;font-weight:700;margin-bottom:16px;letter-spacing:0.05em;text-transform:uppercase">
      SAVE 40% — SPRING SALE
    </div>
    <h1 style="font-size:36px;font-weight:900;line-height:1.15;color:#1a2332;margin:0 0 12px">
      Your New Daily Routine
    </h1>
    <p style="font-size:17px;color:#555;line-height:1.5;margin:0 0 20px">
      Everything your skin needs. Nothing it doesn't.
    </p>
    <div style="display:flex;justify-content:center;align-items:baseline;gap:12px;margin:20px 0">
      <span style="font-size:42px;font-weight:900;color:#1a2332">$89</span>
      <span style="font-size:20px;color:#999;text-decoration:line-through">$149</span>
      <span style="font-size:14px;color:#c53030;font-weight:600">Save $60</span>
    </div>
    <div style="display:flex;justify-content:center;gap:16px;flex-wrap:wrap;margin:16px 0">
      <span style="font-size:13px;color:#555;display:flex;align-items:center;gap:6px">
        <span style="color:#38a169;font-size:14px">✓</span> Free shipping
      </span>
      <span style="font-size:13px;color:#555;display:flex;align-items:center;gap:6px">
        <span style="color:#38a169;font-size:14px">✓</span> 45-day guarantee
      </span>
      <span style="font-size:13px;color:#555;display:flex;align-items:center;gap:6px">
        <span style="color:#38a169;font-size:14px">✓</span> Ships fresh weekly
      </span>
    </div>
    <button style="display:inline-block;background:#c53030;color:#fff;border:none;padding:16px 48px;border-radius:8px;font-size:16px;font-weight:700;cursor:pointer;margin-top:20px;letter-spacing:0.02em">
      Shop Now
    </button>
    <div style="margin-top:14px;font-size:12px;color:#888">
      Free shipping · 45-day guarantee
    </div>
  </div>
</div>`,
  sticky_buy_bar: `<div style="background:#fff;border-bottom:1px solid #e2e2e2;padding:10px 24px;display:flex;justify-content:center;align-items:center;gap:20px;font-family:'Inter',sans-serif">
  <span style="font-size:14px;font-weight:700;color:#1a2332">Your Brand</span>
  <span style="font-size:14px;color:#555">
    <strong>$89</strong>
    <span style="text-decoration:line-through;color:#999;margin-left:6px;font-size:12px">$149</span>
  </span>
  <button style="background:#c53030;color:#fff;border:none;padding:8px 20px;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer">
    Order Now
  </button>
</div>`,
  product_recipe: `<div style="background:#fff;padding:56px 24px;font-family:'Inter',sans-serif">
  <div style="max-width:800px;margin:0 auto">
    <h2 style="font-size:28px;font-weight:800;color:#1a2332;text-align:center;margin-bottom:8px">
      What's Inside
    </h2>
    <p style="font-size:15px;color:#666;text-align:center;margin-bottom:32px">
      Each product works together as a complete daily routine.
    </p>
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:16px">
      <div style="background:#fafaf8;border:1px solid #eee;border-radius:12px;padding:20px 18px">
        <p style="font-size:14px;color:#333;line-height:1.5">Daily Moisturizer — Grass-fed tallow base for deep hydration</p>
      </div>
      <div style="background:#fafaf8;border:1px solid #eee;border-radius:12px;padding:20px 18px">
        <p style="font-size:14px;color:#333;line-height:1.5">Face Balm — Nourishing blend for overnight repair</p>
      </div>
      <div style="background:#fafaf8;border:1px solid #eee;border-radius:12px;padding:20px 18px">
        <p style="font-size:14px;color:#333;line-height:1.5">Body Bar — All-over clean, no synthetics</p>
      </div>
    </div>
  </div>
</div>`,
  how_to_use: `<div style="background:#fafaf8;padding:56px 24px;font-family:'Inter',sans-serif">
  <div style="max-width:700px;margin:0 auto">
    <h2 style="font-size:28px;font-weight:800;color:#1a2332;text-align:center;margin-bottom:8px">
      How to Use
    </h2>
    <p style="font-size:15px;color:#666;text-align:center;margin-bottom:32px">
      A simple daily routine. No complicated steps.
    </p>
    <div style="display:flex;flex-direction:column;gap:16px">
      <div style="display:flex;gap:16px;align-items:flex-start">
        <div style="width:36px;height:36px;border-radius:50%;background:#1a2332;color:#fff;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;flex-shrink:0">1</div>
        <p style="font-size:14px;color:#444;line-height:1.6;padding-top:7px">Wash — Use the body bar with warm water</p>
      </div>
      <div style="display:flex;gap:16px;align-items:flex-start">
        <div style="width:36px;height:36px;border-radius:50%;background:#1a2332;color:#fff;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;flex-shrink:0">2</div>
        <p style="font-size:14px;color:#444;line-height:1.6;padding-top:7px">Moisturize — Apply a small amount of daily moisturizer</p>
      </div>
      <div style="display:flex;gap:16px;align-items:flex-start">
        <div style="width:36px;height:36px;border-radius:50%;background:#1a2332;color:#fff;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;flex-shrink:0">3</div>
        <p style="font-size:14px;color:#444;line-height:1.6;padding-top:7px">Night Repair — Apply face balm before bed</p>
      </div>
    </div>
  </div>
</div>`,
  science_research: `<div style="background:#fff;padding:56px 24px;font-family:'Inter',sans-serif">
  <div style="max-width:700px;margin:0 auto">
    <h2 style="font-size:28px;font-weight:800;color:#1a2332;text-align:center;margin-bottom:8px">
      Backed by Research
    </h2>
    <p style="font-size:15px;color:#666;text-align:center;margin-bottom:32px">
      Ingredients researched from peer-reviewed journals.
    </p>
    <div style="display:flex;flex-direction:column;gap:12px">
      <div style="background:#f8faf8;border:1px solid #e2ede2;border-radius:10px;padding:16px 18px;display:flex;gap:12px;align-items:flex-start">
        <span style="color:#38a169;font-size:18px;flex-shrink:0;margin-top:1px">🔬</span>
        <p style="font-size:14px;color:#444;line-height:1.6">Tallow — Rich in vitamins A, D, E, K. Biocompatible with human skin.</p>
      </div>
      <div style="background:#f8faf8;border:1px solid #e2ede2;border-radius:10px;padding:16px 18px;display:flex;gap:12px;align-items:flex-start">
        <span style="color:#38a169;font-size:18px;flex-shrink:0;margin-top:1px">🔬</span>
        <p style="font-size:14px;color:#444;line-height:1.6">Raw Honey — Natural humectant with antimicrobial properties.</p>
      </div>
    </div>
  </div>
</div>`,
  belief_shift_competitor: `<div style="background:#1a2332;color:#fff;padding:56px 24px;font-family:'Inter',sans-serif">
  <div style="max-width:700px;margin:0 auto;text-align:center">
    <h2 style="font-size:28px;font-weight:800;margin-bottom:8px;line-height:1.2">
      What They Told You vs. What's Actually True
    </h2>
    <p style="font-size:16px;color:rgba(255,255,255,0.7);margin-bottom:32px;line-height:1.6">
      The skincare industry optimized for shareholders, not your skin.
    </p>
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:14px;text-align:left">
      <div style="background:rgba(255,255,255,0.08);border-radius:10px;padding:16px 18px;border:1px solid rgba(255,255,255,0.1)">
        <p style="font-size:14px;color:rgba(255,255,255,0.85);line-height:1.5">They said: "Petroleum-based moisturizers are safe"</p>
      </div>
      <div style="background:rgba(255,255,255,0.08);border-radius:10px;padding:16px 18px;border:1px solid rgba(255,255,255,0.1)">
        <p style="font-size:14px;color:rgba(255,255,255,0.85);line-height:1.5">Truth: Your skin absorbs what you put on it — choose real ingredients.</p>
      </div>
    </div>
  </div>
</div>`,
  farmstand_story: `<div style="background:#f8f6f0;padding:56px 24px;font-family:'Inter',sans-serif">
  <div style="max-width:700px;margin:0 auto;text-align:center">
    <h2 style="font-size:28px;font-weight:800;color:#1a2332;margin-bottom:8px">
      Where It's Made
    </h2>
    <p style="font-size:16px;color:#555;line-height:1.6;margin-bottom:24px">
      Real place. Real people. Made fresh, not warehoused.
    </p>
    <p style="font-size:14px;color:#666;line-height:1.7;margin-bottom:24px;max-width:560px;margin-left:auto;margin-right:auto">
      Every product is handmade in small batches from our family farm. We know every ingredient by name, and we never mass-produce.
    </p>
    <div style="display:flex;flex-direction:column;gap:8px;text-align:left;max-width:500px;margin:0 auto">
      <div style="display:flex;gap:10px;align-items:flex-start">
        <span style="color:#38a169;font-size:14px;margin-top:2px;flex-shrink:0">✓</span>
        <p style="font-size:14px;color:#555;line-height:1.5">Handmade in small batches</p>
      </div>
      <div style="display:flex;gap:10px;align-items:flex-start">
        <span style="color:#38a169;font-size:14px;margin-top:2px;flex-shrink:0">✓</span>
        <p style="font-size:14px;color:#555;line-height:1.5">Family-owned and operated</p>
      </div>
    </div>
  </div>
</div>`,
  timeline_results: `<div style="background:#fff;padding:56px 24px;font-family:'Inter',sans-serif">
  <div style="max-width:600px;margin:0 auto">
    <h2 style="font-size:28px;font-weight:800;color:#1a2332;text-align:center;margin-bottom:8px">
      What to Expect
    </h2>
    <p style="font-size:15px;color:#666;text-align:center;margin-bottom:32px">
      Real results take consistency. Here's the typical timeline.
    </p>
    <div style="display:flex;flex-direction:column;gap:0;position:relative;padding-left:24px">
      <div style="position:absolute;left:10px;top:8px;bottom:8px;width:2px;background:#e0e0e0"></div>
      <div style="display:flex;gap:16px;align-items:flex-start;padding-bottom:24px;position:relative">
        <div style="width:14px;height:14px;border-radius:50%;background:#1a2332;flex-shrink:0;position:absolute;left:-20px;top:4px;border:2px solid #fff;box-shadow:0 0 0 2px #e0e0e0"></div>
        <p style="font-size:14px;color:#444;line-height:1.6;margin-left:8px">Week 1: Softer, more hydrated skin</p>
      </div>
      <div style="display:flex;gap:16px;align-items:flex-start;padding-bottom:24px;position:relative">
        <div style="width:14px;height:14px;border-radius:50%;background:#1a2332;flex-shrink:0;position:absolute;left:-20px;top:4px;border:2px solid #fff;box-shadow:0 0 0 2px #e0e0e0"></div>
        <p style="font-size:14px;color:#444;line-height:1.6;margin-left:8px">Week 2-3: Reduced redness, smoother texture</p>
      </div>
      <div style="display:flex;gap:16px;align-items:flex-start;padding-bottom:24px;position:relative">
        <div style="width:14px;height:14px;border-radius:50%;background:#38a169;flex-shrink:0;position:absolute;left:-20px;top:4px;border:2px solid #fff;box-shadow:0 0 0 2px #e0e0e0"></div>
        <p style="font-size:14px;color:#444;line-height:1.6;margin-left:8px">Month 2: Visible transformation</p>
      </div>
    </div>
  </div>
</div>`,
  quiz_funnel: `<div style="background:linear-gradient(135deg,#f0ece0 0%,#e8e0d0 100%);padding:56px 24px;text-align:center;font-family:'Inter',sans-serif">
  <div style="max-width:500px;margin:0 auto">
    <h2 style="font-size:28px;font-weight:800;color:#1a2332;margin-bottom:8px">
      Find Your Perfect Routine
    </h2>
    <p style="font-size:15px;color:#555;line-height:1.5;margin-bottom:24px">
      Answer a few quick questions and we'll personalize your results.
    </p>
    <div style="background:#fff;border-radius:16px;padding:32px 24px;border:1px solid #e2dcd0;box-shadow:0 4px 20px rgba(0,0,0,0.06)">
      <p style="font-size:14px;color:#888;margin-bottom:16px">Interactive Quiz Preview</p>
      <div style="background:#f5f5f5;border-radius:8px;padding:16px;margin-bottom:16px">
        <p style="font-size:14px;color:#555">What's your biggest skin concern?</p>
      </div>
      <button style="background:#1a2332;color:#fff;border:none;padding:12px 32px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer">
        Take the Quiz
      </button>
    </div>
  </div>
</div>`,
  reviews_grid: `<div style="background:#fafaf8;padding:56px 24px;font-family:'Inter',sans-serif">
  <div style="max-width:800px;margin:0 auto;text-align:center">
    <h2 style="font-size:28px;font-weight:800;color:#1a2332;margin-bottom:4px">
      What Customers Say
    </h2>
    <p style="font-size:15px;color:#888;margin-bottom:32px">
      ⭐ 4.8 · Based on thousands of verified reviews
    </p>
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:16px">
      <div style="background:#fff;border-radius:12px;padding:20px 18px;border:1px solid #eee;text-align:left">
        <div style="color:#f5a623;font-size:14px;margin-bottom:8px">★★★★★</div>
        <p style="font-size:14px;color:#444;line-height:1.6;font-style:italic">"Best product I've ever used. My skin has completely transformed."</p>
        <p style="font-size:12px;color:#aaa;margin-top:10px">Verified Buyer</p>
      </div>
      <div style="background:#fff;border-radius:12px;padding:20px 18px;border:1px solid #eee;text-align:left">
        <div style="color:#f5a623;font-size:14px;margin-bottom:8px">★★★★★</div>
        <p style="font-size:14px;color:#444;line-height:1.6;font-style:italic">"After one week the dry skin is gone. I tell everyone about it."</p>
        <p style="font-size:12px;color:#aaa;margin-top:10px">Verified Buyer</p>
      </div>
      <div style="background:#fff;border-radius:12px;padding:20px 18px;border:1px solid #eee;text-align:left">
        <div style="color:#f5a623;font-size:14px;margin-bottom:8px">★★★★★</div>
        <p style="font-size:14px;color:#444;line-height:1.6;font-style:italic">"Using it at night has transformed the skin around my eyes. Superb!"</p>
        <p style="font-size:12px;color:#aaa;margin-top:10px">Verified Buyer</p>
      </div>
    </div>
  </div>
</div>`,
  faq_accordion: `<div style="background:#fff;padding:56px 24px;font-family:'Inter',sans-serif">
  <div style="max-width:640px;margin:0 auto">
    <h2 style="font-size:28px;font-weight:800;color:#1a2332;text-align:center;margin-bottom:32px">
      Frequently Asked Questions
    </h2>
    <div style="display:flex;flex-direction:column;gap:0">
      <div style="border-bottom:1px solid #eee;padding:18px 0">
        <p style="font-size:15px;font-weight:600;color:#1a2332;margin-bottom:8px">How long until I see results?</p>
        <p style="font-size:14px;color:#666;line-height:1.6">Most notice softer skin within the first week. Deeper changes happen by week 2-3.</p>
      </div>
      <div style="border-bottom:1px solid #eee;padding:18px 0">
        <p style="font-size:15px;font-weight:600;color:#1a2332;margin-bottom:8px">Is this safe for sensitive skin?</p>
        <p style="font-size:14px;color:#666;line-height:1.6">Yes — whole-food ingredients, no harsh chemicals, no fragrances.</p>
      </div>
      <div style="border-bottom:1px solid #eee;padding:18px 0">
        <p style="font-size:15px;font-weight:600;color:#1a2332;margin-bottom:8px">What's the return policy?</p>
        <p style="font-size:14px;color:#666;line-height:1.6">45-day money-back guarantee, no questions asked.</p>
      </div>
    </div>
  </div>
</div>`,
  final_cta: `<div style="background:linear-gradient(135deg,#1a2332 0%,#2d3748 100%);color:#fff;padding:64px 24px;text-align:center;font-family:'Inter',sans-serif">
  <div style="max-width:600px;margin:0 auto">
    <h2 style="font-size:32px;font-weight:900;margin-bottom:12px;line-height:1.2">
      Don't Wait — This Offer Won't Last
    </h2>
    <p style="font-size:16px;color:rgba(255,255,255,0.7);margin-bottom:24px;line-height:1.5">
      Your skin deserves better. Start today.
    </p>
    <div style="display:flex;justify-content:center;align-items:baseline;gap:12px;margin-bottom:20px">
      <span style="font-size:42px;font-weight:900">$89</span>
      <span style="font-size:20px;color:rgba(255,255,255,0.5);text-decoration:line-through">$149</span>
    </div>
    <div style="display:flex;flex-direction:column;gap:6px;align-items:center;margin-bottom:24px">
      <span style="font-size:14px;color:rgba(255,255,255,0.8);display:flex;align-items:center;gap:8px">
        <span style="color:#68d391">✓</span> Free shipping
      </span>
      <span style="font-size:14px;color:rgba(255,255,255,0.8);display:flex;align-items:center;gap:8px">
        <span style="color:#68d391">✓</span> 45-day money-back guarantee
      </span>
    </div>
    <button style="display:inline-block;background:#c53030;color:#fff;border:none;padding:16px 48px;border-radius:8px;font-size:16px;font-weight:700;cursor:pointer;letter-spacing:0.02em">
      Order Now
    </button>
    <div style="margin-top:16px;font-size:12px;color:rgba(255,255,255,0.5)">
      Free shipping · 45-day money-back guarantee
    </div>
  </div>
</div>`,
};

const ALL_SECTION_TYPES = Object.keys(SECTION_LABELS);

type TemplateSection = {
  sectionType: string;
  order: number;
  enabled: boolean;
  variant: string;
};

function SectionHtmlEditor({
  sectionType,
  html,
  onChange,
}: {
  sectionType: string;
  html: string;
  onChange: (html: string) => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [localHtml, setLocalHtml] = useState(html);
  const updateTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setLocalHtml(html);
  }, [html]);

  useEffect(() => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Inter', -apple-system, sans-serif; }
  </style>
</head>
<body>${localHtml}</body>
</html>`);
        doc.close();
      }
    }
  }, [localHtml]);

  const handleChange = (value: string) => {
    setLocalHtml(value);
    if (updateTimer.current) clearTimeout(updateTimer.current);
    updateTimer.current = setTimeout(() => onChange(value), 600);
  };

  return (
    <div className={`${expanded ? "fixed inset-0 z-50 bg-background p-4 flex flex-col" : ""}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Code className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">{SECTION_LABELS[sectionType] || sectionType}</span>
        </div>
        <div className="flex items-center gap-1">
          {!html && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => handleChange(DEFAULT_SECTION_HTML[sectionType] || `<div style="padding:40px 24px;text-align:center"><h2>${SECTION_LABELS[sectionType] || sectionType}</h2></div>`)}
              data-testid={`button-load-default-${sectionType}`}
            >
              Load Default
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setExpanded(!expanded)}
            data-testid={`button-expand-${sectionType}`}
          >
            {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>
      <div className={`grid grid-cols-2 gap-3 ${expanded ? "flex-1 min-h-0" : ""}`}>
        <div className="flex flex-col min-h-0">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Code className="h-3 w-3 text-muted-foreground/50" />
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/50">HTML</span>
          </div>
          <textarea
            value={localHtml}
            onChange={e => handleChange(e.target.value)}
            className={`flex-1 w-full rounded-lg border border-border/40 bg-muted/20 p-3 font-mono text-xs leading-relaxed resize-none focus:outline-none focus:ring-1 focus:ring-primary/30 ${expanded ? "min-h-0" : "min-h-[280px]"}`}
            spellCheck={false}
            data-testid={`textarea-html-${sectionType}`}
          />
        </div>
        <div className="flex flex-col min-h-0">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Eye className="h-3 w-3 text-muted-foreground/50" />
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/50">Preview</span>
          </div>
          <iframe
            ref={iframeRef}
            className={`flex-1 w-full rounded-lg border border-border/40 bg-white ${expanded ? "min-h-0" : "min-h-[280px]"}`}
            sandbox="allow-same-origin"
            title={`Preview ${sectionType}`}
            data-testid={`iframe-preview-${sectionType}`}
          />
        </div>
      </div>
    </div>
  );
}

function FullPagePreview({
  sections,
  sectionHtml,
}: {
  sections: TemplateSection[];
  sectionHtml: Record<string, string>;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const enabledSections = sections.filter(s => s.enabled).sort((a, b) => a.order - b.order);

  const fullHtml = useMemo(() => {
    const body = enabledSections
      .map(s => sectionHtml[s.sectionType] || `<div style="padding:40px 24px;text-align:center;border:1px dashed #ddd;font-family:'Inter',sans-serif"><p style="font-size:14px;color:#aaa">${SECTION_LABELS[s.sectionType] || s.sectionType}</p><p style="font-size:12px;color:#ccc;margin-top:4px">No HTML template yet</p></div>`)
      .join("\n");
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Inter', -apple-system, sans-serif; }
  </style>
</head>
<body>${body}</body>
</html>`;
  }, [enabledSections, sectionHtml]);

  useEffect(() => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(fullHtml);
        doc.close();
      }
    }
  }, [fullHtml]);

  return (
    <iframe
      ref={iframeRef}
      className="w-full rounded-xl border border-border/40 bg-white"
      style={{ height: "80vh" }}
      sandbox="allow-same-origin"
      title="Full page preview"
      data-testid="iframe-full-preview"
    />
  );
}

export default function LandingPageTemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();

  const { data: template, isLoading } = useQuery<LandingPageTemplate>({
    queryKey: ["/api/landing-page-templates", id],
    queryFn: async () => {
      const res = await fetch(`/api/landing-page-templates/${id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch template");
      return res.json();
    },
    enabled: !!id,
  });

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sections, setSections] = useState<TemplateSection[]>([]);
  const [formulas, setFormulas] = useState<Record<string, string>>({});
  const [sectionHtml, setSectionHtml] = useState<Record<string, string>>({});
  const [isDefault, setIsDefault] = useState(false);
  const [activeTab, setActiveTab] = useState("sections");
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();
  const isInitial = useRef(true);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || "");
      setSections((template.sections as TemplateSection[]) || []);
      setFormulas((template.sectionFormulas as Record<string, string>) || {});
      setSectionHtml((template.sectionHtml as Record<string, string>) || {});
      setIsDefault(template.isDefault);
      isInitial.current = true;
    }
  }, [template]);

  const save = useCallback(async (data: {
    name: string;
    description: string;
    sections: TemplateSection[];
    sectionFormulas: Record<string, string>;
    sectionHtml: Record<string, string>;
    isDefault: boolean;
  }) => {
    try {
      await apiRequest("PATCH", `/api/landing-page-templates/${id}`, {
        name: data.name,
        description: data.description || null,
        sections: data.sections,
        sectionFormulas: data.sectionFormulas,
        sectionHtml: data.sectionHtml,
        isDefault: data.isDefault,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/landing-page-templates", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/landing-page-templates"] });
    } catch {
    }
  }, [id]);

  useEffect(() => {
    if (isInitial.current) { isInitial.current = false; return; }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => save({ name, description, sections, sectionFormulas: formulas, sectionHtml, isDefault }), 800);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [name, description, sections, formulas, sectionHtml, isDefault, save]);

  const addSection = (type: string) => {
    const maxOrder = sections.reduce((max, s) => Math.max(max, s.order), -1);
    setSections([...sections, { sectionType: type, order: maxOrder + 1, enabled: true, variant: "default" }]);
  };

  const removeSection = (index: number) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const toggleSection = (index: number, enabled: boolean) => {
    setSections(sections.map((s, i) => i === index ? { ...s, enabled } : s));
  };

  const updateVariant = (index: number, variant: string) => {
    setSections(sections.map((s, i) => i === index ? { ...s, variant } : s));
  };

  const handleReorder = (reordered: TemplateSection[]) => {
    setSections(reordered.map((s, i) => ({ ...s, order: i })));
  };

  const updateFormula = (sectionType: string, formula: string) => {
    setFormulas(prev => ({ ...prev, [sectionType]: formula }));
  };

  const updateSectionHtml = (sectionType: string, html: string) => {
    setSectionHtml(prev => ({ ...prev, [sectionType]: html }));
  };

  const loadAllDefaults = () => {
    const newHtml: Record<string, string> = { ...sectionHtml };
    sections.forEach(s => {
      if (!newHtml[s.sectionType] && DEFAULT_SECTION_HTML[s.sectionType]) {
        newHtml[s.sectionType] = DEFAULT_SECTION_HTML[s.sectionType];
      }
    });
    setSectionHtml(newHtml);
    toast({ title: "Loaded default HTML for all sections" });
  };

  const usedTypes = new Set(sections.map(s => s.sectionType));
  const availableTypes = ALL_SECTION_TYPES.filter(t => !usedTypes.has(t));

  if (isLoading) {
    return (
      <div className="p-8 max-w-5xl mx-auto space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[400px] rounded-xl" />
      </div>
    );
  }

  if (!template) {
    return <div className="p-8 max-w-5xl mx-auto text-center py-12 text-muted-foreground">Template not found</div>;
  }

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link href="/landing-page-templates">
          <Button variant="ghost" size="icon" className="shrink-0 rounded-full" data-testid="button-back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              className="text-xl font-bold border-none shadow-none px-0 h-auto focus-visible:ring-0"
              data-testid="input-template-name"
            />
          </div>
          <p className="text-sm text-muted-foreground">Template configuration</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Default</Label>
          <Switch
            checked={isDefault}
            onCheckedChange={setIsDefault}
            data-testid="switch-is-default"
          />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="template-tabs">
          <TabsTrigger value="sections">Sections</TabsTrigger>
          <TabsTrigger value="html-editor">HTML Editor</TabsTrigger>
          <TabsTrigger value="preview">Full Preview</TabsTrigger>
          <TabsTrigger value="formulas">AI Formulas</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="sections" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Drag to reorder. Toggle to enable/disable sections by default.
            </p>
            {availableTypes.length > 0 && (
              <SearchableSelect
                value=""
                onValueChange={addSection}
                placeholder="Add section..."
                testId="select-add-section"
                triggerClassName="w-48"
                options={availableTypes.map(type => ({
                  value: type,
                  label: SECTION_LABELS[type] || type,
                }))}
              />
            )}
          </div>

          <Reorder.Group axis="y" values={sections} onReorder={handleReorder} className="space-y-2">
            {sections.map((section, index) => (
              <Reorder.Item key={section.sectionType} value={section} className="list-none">
                <Card className={`border-border/40 transition-opacity ${!section.enabled ? "opacity-50" : ""}`} data-testid={`card-section-${section.sectionType}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <GripVertical className="h-4 w-4 text-muted-foreground/40 cursor-grab shrink-0" />
                    <span className="text-xs text-muted-foreground/40 w-5 shrink-0">{index + 1}</span>
                    <span className="text-sm font-medium flex-1">{SECTION_LABELS[section.sectionType] || section.sectionType}</span>
                    {sectionHtml[section.sectionType] && (
                      <span className="text-[10px] text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 dark:text-emerald-400 px-1.5 py-0.5 rounded font-medium">HTML</span>
                    )}
                    <Select value={section.variant || "default"} onValueChange={v => updateVariant(index, v)}>
                      <SelectTrigger className="w-24 h-7 text-[11px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Default</SelectItem>
                        <SelectItem value="minimal">Minimal</SelectItem>
                      </SelectContent>
                    </Select>
                    <Switch
                      checked={section.enabled}
                      onCheckedChange={v => toggleSection(index, v)}
                      data-testid={`switch-${section.sectionType}`}
                    />
                    <button
                      type="button"
                      className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground/30 hover:text-destructive hover:bg-destructive/10 transition-all"
                      onClick={() => removeSection(index)}
                      data-testid={`button-remove-${section.sectionType}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </CardContent>
                </Card>
              </Reorder.Item>
            ))}
          </Reorder.Group>

          {sections.length === 0 && (
            <Card className="border-dashed border-border/50">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <p className="text-sm text-muted-foreground">No sections. Use the dropdown above to add sections.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="html-editor" className="space-y-6 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Edit the HTML for each section. Changes preview live on the right.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={loadAllDefaults}
              data-testid="button-load-all-defaults"
            >
              <Plus className="h-3 w-3 mr-1.5" />
              Load All Defaults
            </Button>
          </div>

          {editingSection ? (
            <div>
              <Button
                variant="ghost"
                size="sm"
                className="mb-3 h-7 text-xs"
                onClick={() => setEditingSection(null)}
              >
                <ArrowLeft className="h-3 w-3 mr-1" />
                Back to all sections
              </Button>
              <SectionHtmlEditor
                sectionType={editingSection}
                html={sectionHtml[editingSection] || ""}
                onChange={html => updateSectionHtml(editingSection, html)}
              />
            </div>
          ) : (
            <div className="space-y-2">
              {sections.filter(s => s.enabled).sort((a, b) => a.order - b.order).map(section => (
                <Card
                  key={section.sectionType}
                  className="border-border/40 cursor-pointer hover:bg-muted/20 transition-colors"
                  onClick={() => setEditingSection(section.sectionType)}
                  data-testid={`card-html-${section.sectionType}`}
                >
                  <CardContent className="p-3 flex items-center gap-3">
                    <Code className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                    <span className="text-sm font-medium flex-1">{SECTION_LABELS[section.sectionType] || section.sectionType}</span>
                    {sectionHtml[section.sectionType] ? (
                      <span className="text-[10px] text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 dark:text-emerald-400 px-1.5 py-0.5 rounded font-medium">
                        {sectionHtml[section.sectionType].length.toLocaleString()} chars
                      </span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground/40 px-1.5 py-0.5">No HTML</span>
                    )}
                    <Button variant="ghost" size="sm" className="h-7 text-xs" data-testid={`button-edit-html-${section.sectionType}`}>
                      Edit
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="preview" className="space-y-4 mt-4">
          <p className="text-sm text-muted-foreground">
            Full-page preview of all enabled sections in order.
          </p>
          <FullPagePreview sections={sections} sectionHtml={sectionHtml} />
        </TabsContent>

        <TabsContent value="formulas" className="space-y-4 mt-4">
          <p className="text-sm text-muted-foreground">
            AI draft formulas define what the AI generates for each section type. These are the instructions sent to the AI when drafting copy.
          </p>
          <Accordion type="multiple" className="space-y-2">
            {sections.filter(s => s.enabled).map(section => (
              <AccordionItem key={section.sectionType} value={section.sectionType} className="border border-border/40 rounded-xl px-4">
                <AccordionTrigger className="text-sm font-medium py-3" data-testid={`accordion-formula-${section.sectionType}`}>
                  {SECTION_LABELS[section.sectionType] || section.sectionType}
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <Textarea
                    value={formulas[section.sectionType] || ""}
                    onChange={e => updateFormula(section.sectionType, e.target.value)}
                    placeholder="Enter the AI draft formula for this section..."
                    rows={4}
                    className="text-xs"
                    data-testid={`textarea-formula-${section.sectionType}`}
                  />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </TabsContent>

        <TabsContent value="settings" className="space-y-5 mt-4 max-w-2xl">
          <Card className="rounded-xl border border-border/40 bg-card">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-primary/8 flex items-center justify-center">
                  <LayoutTemplate className="h-3 w-3 text-primary" />
                </div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Template Info</p>
              </div>
              <div className="pl-7 space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    data-testid="input-settings-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Description</Label>
                  <Textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Describe what this template is for..."
                    rows={3}
                    data-testid="input-settings-description"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={isDefault}
                    onCheckedChange={setIsDefault}
                    data-testid="switch-settings-default"
                  />
                  <Label className="text-xs text-muted-foreground">Set as default template for new landing pages</Label>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
